import React, { useEffect, useState } from 'react';
import { Copy, Key, Plus, Trash2, AlertCircle, CheckCircle, Clock, Shield, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import { tokenService } from '@/services/tokenService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import type { ApiTokenInfo, CreateApiTokenRequest } from '@/types/WeeklyReport';
import { TOKEN_EXPIRATION_OPTIONS, DEFAULT_TOKEN_SCOPES } from '@/types/WeeklyReport';

const ApiTokensPage: React.FC = () => {
    const [tokens, setTokens] = useState<ApiTokenInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newToken, setNewToken] = useState<string | null>(null);
    
    // Form state
    const [tokenName, setTokenName] = useState('');
    const [expiresInDays, setExpiresInDays] = useState(30);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        loadTokens();
    }, []);

    const loadTokens = async () => {
        setIsLoading(true);
        try {
            const tokenList = await tokenService.listTokens();
            setTokens(tokenList);
        } catch (error) {
            console.error('Error loading tokens:', error);
            toast.error('Failed to load API tokens');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateToken = async () => {
        if (!tokenName.trim()) {
            toast.error('Please enter a token name');
            return;
        }

        setIsCreating(true);
        try {
            const request: CreateApiTokenRequest = {
                name: tokenName.trim(),
                expiresInDays,
                scopes: DEFAULT_TOKEN_SCOPES // Always use default (reports:write only)
            };

            const result = await tokenService.createToken(request);
            setNewToken(result.token);
            toast.success('API token created successfully');
            
            // Reset form
            setTokenName('');
            setExpiresInDays(30);
            
            // Refresh token list
            await loadTokens();
        } catch (error) {
            console.error('Error creating token:', error);
            toast.error('Failed to create API token');
        } finally {
            setIsCreating(false);
        }
    };

    const handleRevokeToken = async (tokenId: string, tokenName: string) => {
        if (!confirm(`Are you sure you want to revoke the token "${tokenName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await tokenService.revokeToken(tokenId);
            toast.success('Token revoked successfully');
            await loadTokens();
        } catch (error) {
            console.error('Error revoking token:', error);
            toast.error('Failed to revoke token');
        }
    };

    const handleCopyToken = async () => {
        if (newToken) {
            await navigator.clipboard.writeText(newToken);
            toast.success('Token copied to clipboard');
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setNewToken(null);
        setTokenName('');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container mx-auto py-8 px-4 max-w-5xl">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Key className="h-8 w-8" />
                        API Tokens
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage API tokens for programmatic access to your reports
                    </p>
                </div>

                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button onClick={() => setIsDialogOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Token
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                        {newToken ? (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2 text-green-600">
                                        <CheckCircle className="h-5 w-5" />
                                        Token Created
                                    </DialogTitle>
                                    <DialogDescription>
                                        Copy your new API token now. You won&apos;t be able to see it again!
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="py-4">
                                    <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
                                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                                        <AlertTitle className="text-yellow-800 dark:text-yellow-200">Important</AlertTitle>
                                        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                                            This token will only be displayed once. Make sure to copy it now!
                                        </AlertDescription>
                                    </Alert>
                                    <div className="mt-4 p-3 bg-muted rounded-lg font-mono text-sm break-all">
                                        {newToken}
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={handleCopyToken}>
                                        <Copy className="h-4 w-4 mr-2" />
                                        Copy Token
                                    </Button>
                                    <Button onClick={handleDialogClose}>Done</Button>
                                </DialogFooter>
                            </>
                        ) : (
                            <>
                                <DialogHeader>
                                    <DialogTitle>Create New API Token</DialogTitle>
                                    <DialogDescription>
                                        Generate a token for programmatic access to the Weekly Report API.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="token-name">Token Name</Label>
                                        <Input
                                            id="token-name"
                                            placeholder="e.g., CI/CD Pipeline, Automation Script"
                                            value={tokenName}
                                            onChange={(e) => setTokenName(e.target.value)}
                                        />
                                        <p className="text-sm text-muted-foreground">
                                            A descriptive name to identify this token
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="expiration">Expiration</Label>
                                        <Select
                                            value={String(expiresInDays)}
                                            onValueChange={(v) => setExpiresInDays(Number(v))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TOKEN_EXPIRATION_OPTIONS.map(opt => (
                                                    <SelectItem key={opt.days} value={String(opt.days)}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="p-3 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm">
                                            <Shield className="h-4 w-4 text-muted-foreground" />
                                            <span className="font-medium">Permission:</span>
                                            <Badge variant="secondary">reports:write</Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            This token can only submit weekly reports via the API.
                                        </p>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={handleDialogClose}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleCreateToken} disabled={isCreating}>
                                        {isCreating ? 'Creating...' : 'Create Token'}
                                    </Button>
                                </DialogFooter>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            </div>

            {/* API Usage Guide */}
            <Card className="mb-6 border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Terminal className="h-5 w-5" />
                        API Usage
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        Use your API token to submit reports programmatically:
                    </p>
                    <div className="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                        <pre>{`curl -X POST "https://your-domain.com/api/v1/reports" \\
    -H "Authorization: Bearer wrpt_your_token_here" \\
    -H "Content-Type: application/json" \\
    -d '{
      "submission": {
        "sendEmail": true,
        "recipients": { "to": ["your.email@example.com"] },
        "report": {
          "title": "Curl Test",
          "clientName": "Test Client",
          "dateRange": { "from": "2025-11-30", "to": "2025-01-04" },
          "lastWeekTasks": [{ "value": "Deployed feature X" }],
          "unplannedTasks": [],
          "blockers": [],
          "nextWeekTasks": [{ "value": "Start feature Z" }],
          "furtherBeyond": [],
          "notes": "Optional notes"
        }
      }
    }'`}</pre>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        See the full API documentation for all available endpoints and options.
                    </p>
                </CardContent>
            </Card>

            {/* Token List */}
            <Card>
                <CardHeader>
                    <CardTitle>Your Tokens</CardTitle>
                    <CardDescription>
                        Active API tokens associated with your account
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                            Loading tokens...
                        </div>
                    ) : tokens.length === 0 ? (
                        <div className="text-center py-8">
                            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">No API tokens yet</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Create a token to start using the API
                            </p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Token</TableHead>
                                    <TableHead>Scopes</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Expires</TableHead>
                                    <TableHead>Last Used</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tokens.map(token => (
                                    <TableRow key={token.id}>
                                        <TableCell className="font-medium">{token.name}</TableCell>
                                        <TableCell>
                                            <code className="text-xs bg-muted px-2 py-1 rounded">
                                                {token.tokenPrefix}
                                            </code>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {token.scopes.map(scope => (
                                                    <Badge key={scope} variant="outline" className="text-xs">
                                                        {scope}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {formatDate(token.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            {token.isExpired ? (
                                                <Badge variant="destructive" className="text-xs">
                                                    Expired
                                                </Badge>
                                            ) : (
                                                <span className="text-sm text-muted-foreground flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {formatDate(token.expiresAt)}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {token.lastUsedAt ? formatDate(token.lastUsedAt) : 'Never'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRevokeToken(token.id, token.name)}
                                                className="text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ApiTokensPage;

